import { NgClass } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { Tag } from 'primeng/tag';
import FirmwareRolloutDetailDTO from '../../../../../types/firmware-rollout/FirmwareRolloutDetailDTO';
import FirmwareRolloutDTO from '../../../../../types/firmware-rollout/FirmwareRolloutDTO';
import FirmwareRolloutSampleEventDTO from '../../../../../types/firmware-rollout/FirmwareRolloutSampleEventDTO';
import RolloutPhase from '../../../../../types/firmware-rollout/RolloutPhase';
import RolloutSampleStatus from '../../../../../types/firmware-rollout/RolloutSampleStatus';
import RolloutSampleStatusDTO from '../../../../../types/firmware-rollout/RolloutSampleStatusDTO';
import RolloutStatus from '../../../../../types/firmware-rollout/RolloutStatus';
import SampleFirmwareUpgradeRequest from '../../../../../types/sample-firmware-upgrade/SampleFirmwareUpgradeRequest';
import StudioUpgradeTarget from '../../../../../types/sample-firmware-upgrade/StudioUpgradeTarget';
import { EnumLabelPipe } from '../../../pipes/enum-label.pipe';
import { FormatDateTime } from '../../../pipes/format-date-time';
import { FirmwareRolloutService } from '../../../services/firmware-rollout.service';
import { PhysicalSampleService } from '../../../services/physical-sample.service';
import { SampleFirmwareRevisionService } from '../../../services/sample-firmware-revision.service';
import { PageHeaderComponent } from '../../shared/page-header/page-header.component';

// Sorts samples needing attention (running/failed) ahead of already-successful/cancelled ones.
const STATUS_PRIORITY: Record<RolloutSampleStatus, number> = {
    [RolloutSampleStatus.ERROR]: 0,
    [RolloutSampleStatus.START]: 1,
    [RolloutSampleStatus.PENDING]: 2,
    [RolloutSampleStatus.SUCCESS]: 3,
    [RolloutSampleStatus.CANCELLED]: 4
};

const CANCELLABLE_ROLLOUT_STATUSES = [
    RolloutStatus.PENDING,
    RolloutStatus.IN_PROGRESS
];

const TERMINAL_ROLLOUT_STATUSES = [
    RolloutStatus.COMPLETED,
    RolloutStatus.COMPLETED_WITH_ERRORS,
    RolloutStatus.CANCELLED
];
const POLL_INTERVAL_MS = 5000;

@Component({
    selector: 'app-rollout-detail',
    templateUrl: './rollout-detail.component.html',
    styleUrls: [ './rollout-detail.component.scss' ],
    imports: [
        PageHeaderComponent,
        Button,
        RouterLink,
        TableModule,
        Tag,
        FormatDateTime,
        NgClass,
        EnumLabelPipe
    ]
})
export class RolloutDetailComponent implements OnInit, OnDestroy {

    protected readonly RolloutStatus = RolloutStatus;
    protected readonly RolloutSampleStatus = RolloutSampleStatus;

    public rolloutDetail: FirmwareRolloutDetailDTO | null = null;
    public sortedSamples: RolloutSampleStatusDTO[] = [];
    public loading = false;
    // Unlike `loading`, also lights up during silent poll ticks to keep the page feeling "live".
    public refreshing = false;
    public expandedSampleId: number | null = null;
    public historyBySampleId: Map<number, FirmwareRolloutSampleEventDTO[]> = new Map();
    public loadingHistoryForSampleId: number | null = null;
    public retryingFailedSamples = false;
    public cancellingRollout = false;

    private rolloutId!: number;
    private pollHandle: ReturnType<typeof setInterval> | null = null;
    private polling = false;

    constructor(
        private route: ActivatedRoute,
        private firmwareRolloutService: FirmwareRolloutService,
        private physicalSampleService: PhysicalSampleService,
        private sampleFirmwareRevisionService: SampleFirmwareRevisionService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService
    ) {
    }

    public ngOnInit(): void {
        const rolloutIdParam = this.route.snapshot.paramMap.get('rolloutId');
        if (!rolloutIdParam) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Missing rollout id' });
            return;
        }
        this.rolloutId = Number(rolloutIdParam);
        this.loadRolloutDetail();
        // Polls silently until the rollout reaches a terminal state; skips overlapping ticks.
        this.pollHandle = setInterval(() => {
            if (this.polling) {
                return;
            }
            if (this.rolloutDetail && TERMINAL_ROLLOUT_STATUSES.includes(this.rolloutDetail.rollout.status)) {
                return;
            }
            this.polling = true;
            this.loadRolloutDetail(true).finally(() => {
                this.polling = false;
            });
        }, POLL_INTERVAL_MS);
    }

    public ngOnDestroy(): void {
        if (this.pollHandle != null) {
            clearInterval(this.pollHandle);
        }
    }

    public get canCancel(): boolean {
        return !!this.rolloutDetail && CANCELLABLE_ROLLOUT_STATUSES.includes(this.rolloutDetail.rollout.status);
    }

    public progressPercent(): number {
        const counts = this.rolloutDetail?.rollout.sampleCounts;
        if (!counts || counts.total === 0) {
            return 0;
        }
        return Math.round(( ( counts.success + counts.error + counts.cancelled ) / counts.total ) * 100);
    }

    // Explains what the Hub is actually doing right now, since the raw status/counts alone don't say.
    public phaseLabel(): string {
        const rollout = this.rolloutDetail?.rollout;
        if (!rollout) {
            return '';
        }
        switch (rollout.phase) {
            case RolloutPhase.AWAITING_DISPATCH:
                return 'Preparing rollout - transferring the firmware file to the Hub and waiting for it to confirm receipt...';
            case RolloutPhase.PILOT_IN_PROGRESS:
                return `Flashing pilot ${ rollout.pilotMacAddress ?? '' }...`;
            case RolloutPhase.PILOT_FAILED:
                return `Pilot ${ rollout.pilotMacAddress ?? '' } failed - rollout stalled, needs attention`;
            case RolloutPhase.BATCH_IN_PROGRESS:
                return 'Flashing batch...';
            default:
                return '';
        }
    }

    /**
     * Only stops future batches from being dispatched - a batch already broadcast to a sample can't be
     * recalled, so that sample still reports its real outcome even after the rollout is cancelled.
     */
    public cancelRollout(): void {
        if (!this.rolloutDetail) {
            return;
        }
        this.confirmationService.confirm({
            header: 'Cancel Rollout',
            message: `Cancel this rollout targeting '${ this.rolloutDetail.rollout.targetReleaseVersion }' in `
                + `'${ this.rolloutDetail.rollout.studioName }'? Samples already mid-flash will still report `
                + `their real outcome - this only stops any batch not yet dispatched.`,
            icon: 'pi pi-exclamation-triangle',
            accept: () => this.submitCancel()
        });
    }

    /**
     * Re-submits a firmware upgrade request scoped to just this rollout's currently-failed MAC
     * addresses (e.g. samples that timed out while offline), sparing the operator from having to
     * hunt those MACs down and re-select them via the general physical-samples upgrade dialog.
     */
    public retryFailedSamples(): void {
        const rollout = this.rolloutDetail?.rollout;
        if (!rollout) {
            return;
        }
        const failedMacAddresses = this.sortedSamples
            .filter(sample => sample.currentStatus === RolloutSampleStatus.ERROR)
            .map(sample => sample.macAddress);
        if (!failedMacAddresses.length) {
            return;
        }
        this.confirmationService.confirm({
            header: 'Retry Failed Samples',
            message: `Retry the firmware upgrade to '${ rollout.targetReleaseVersion }' for `
                + `${ failedMacAddresses.length } failed sample(s) in '${ rollout.studioName }'?`,
            icon: 'pi pi-exclamation-triangle',
            accept: () => this.submitRetry(rollout, failedMacAddresses)
        });
    }

    public toggleSampleHistory(physicalSampleId: number): void {
        if (this.expandedSampleId === physicalSampleId) {
            this.expandedSampleId = null;
            return;
        }
        this.expandedSampleId = physicalSampleId;
        if (this.historyBySampleId.has(physicalSampleId)) {
            return;
        }
        this.loadingHistoryForSampleId = physicalSampleId;
        this.firmwareRolloutService.getSampleHistory(this.rolloutId, physicalSampleId)
            .then(events => this.historyBySampleId.set(physicalSampleId, events))
            .catch((error: any) => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message });
            })
            .finally(() => {
                this.loadingHistoryForSampleId = null;
            });
    }

    private submitCancel(): void {
        this.cancellingRollout = true;
        this.firmwareRolloutService.cancelRollout(this.rolloutId)
            .then(() => {
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Rollout cancelled' });
                return this.loadRolloutDetail(true);
            })
            .catch((error: any) => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message });
            })
            .finally(() => {
                this.cancellingRollout = false;
            });
    }

    private submitRetry(rollout: FirmwareRolloutDTO, macAddresses: string[]): void {
        if (rollout.firmwareRevisionId == null) {
            this.messageService.add({
                severity: 'error', summary: 'Error',
                detail: 'This rollout has no associated firmware revision to retry.'
            });
            return;
        }
        const firmwareRevisionId = rollout.firmwareRevisionId;
        this.retryingFailedSamples = true;
        Promise.all([
            this.sampleFirmwareRevisionService.getSampleFirmwareRevision(firmwareRevisionId),
            // Re-checked since the sample may have already been upgraded elsewhere - avoids a wasted, risky re-flash.
            this.physicalSampleService.getEligiblePhysicalSamples(rollout.studioId, firmwareRevisionId)
        ])
            .then(([ firmwareRevision, eligibleSamples ]) => {
                const eligibleMacAddresses = new Set(
                    eligibleSamples.map(sample => sample.macAddress).filter((mac): mac is string => !!mac));
                const stillEligibleMacAddresses = macAddresses.filter(mac => eligibleMacAddresses.has(mac));
                const alreadyUpToDateCount = macAddresses.length - stillEligibleMacAddresses.length;
                if (!stillEligibleMacAddresses.length) {
                    this.messageService.add({
                        severity: 'info', summary: 'Nothing to retry',
                        detail: 'All previously failed samples are already on this firmware version.'
                    });
                    return null;
                }
                const request = new SampleFirmwareUpgradeRequest({
                    firmwareRevisionId: firmwareRevision.id,
                    firmwareFileName: firmwareRevision.fileName,
                    targetReleaseVersion: firmwareRevision.releaseVersion,
                    immediate: rollout.immediate,
                    studioTargets: [
                        new StudioUpgradeTarget({
                            studioId: rollout.studioId,
                            allEligibleSamples: false,
                            macAddresses: stillEligibleMacAddresses
                        })
                    ]
                });
                return this.sampleFirmwareRevisionService.createSampleFirmwareUpgradeRequest(request)
                    .then(() => {
                        const skippedNote = alreadyUpToDateCount > 0 ?
                            ` (${ alreadyUpToDateCount } already up to date, skipped)` : '';
                        this.messageService.add({
                            severity: 'success', summary: 'Success',
                            detail: `Retry submitted for ${ stillEligibleMacAddresses.length } sample(s)${ skippedNote }.`
                        });
                        return this.loadRolloutDetail(true);
                    });
            })
            .catch((error: any) => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message });
            })
            .finally(() => {
                this.retryingFailedSamples = false;
            });
    }

    private loadRolloutDetail(silent: boolean = false): Promise<void> {
        if (!silent) {
            this.loading = true;
        }
        this.refreshing = true;
        return this.firmwareRolloutService.getRolloutDetail(this.rolloutId)
            .then(detail => {
                this.rolloutDetail = detail;
                this.sortedSamples = [ ...detail.samples ].sort((a, b) => {
                    if (a.pilot !== b.pilot) {
                        return a.pilot ? -1 : 1;
                    }
                    const priorityDiff = ( STATUS_PRIORITY[a.currentStatus] ?? 99 ) -
                        ( STATUS_PRIORITY[b.currentStatus] ?? 99 );
                    return priorityDiff !== 0 ? priorityDiff : a.macAddress.localeCompare(b.macAddress);
                });
            })
            .catch((error: any) => {
                if (!silent) {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message });
                }
            })
            .finally(() => {
                this.loading = false;
                this.refreshing = false;
            });
    }

}
